/**
 * Modal pour créer ou modifier une permission
 */

import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { PermissionModalProps, PermissionFormData } from '@/types/permissionManagement';

const PermissionModal: React.FC<PermissionModalProps> = ({
  isOpen,
  onClose,
  permission,
  onSave
}) => {
  const [formData, setFormData] = useState<PermissionFormData>({
    code: '',
    name: '',
    display_name: '',
    description: '',
    category: '',
    access_level: 'read'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  // Catégories prédéfinies (correspondant aux CATEGORY_CHOICES du backend)
  const categories = [
    { value: 'ACCESS', label: 'Accès' },
    { value: 'USER_MANAGEMENT', label: 'Gestion des utilisateurs' },
    { value: 'SYSTEM_CONFIG', label: 'Configuration système' },
    { value: 'WEIGHING', label: 'Pesée' },
    { value: 'QUALITY_ANALYSIS', label: 'Analyse qualité' },
    { value: 'TICKET_GENERATION', label: 'Génération de tickets' },
    { value: 'WEIGHING_CANCELLATION', label: 'Annulation de pesée' },
    { value: 'REJECTION', label: 'Refoulement' },
    { value: 'STOCK_MANAGEMENT', label: 'Gestion du stock' },
    { value: 'STATISTICS_REPORTS', label: 'Statistiques & Rapports' },
    { value: 'EXPORT', label: 'Export Excel / PDF' },
    { value: 'MULTI_SITE', label: 'Gestion multi-sites' },
    { value: 'AUDIT', label: 'Audit' }
  ];

  const accessLevels = [
    { value: 'read', label: 'Lecture' },
    { value: 'write', label: 'Écriture' },
    { value: 'admin', label: 'Administration' }
  ];

  useEffect(() => {
    if (permission) {
      setFormData({
        code: permission.code,
        name: permission.name,
        display_name: permission.display_name || '',
        description: permission.description,
        category: permission.category,
        access_level: (permission.access_level?.toLowerCase() as 'read' | 'write' | 'admin') || 'read'
      });
    } else {
      setFormData({
        code: '',
        name: '',
        display_name: '',
        description: '',
        category: '',
        access_level: 'read'
      });
    }
    setErrors({});
  }, [permission, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Le code est requis';
    } else if (!/^[a-z_]+\.[a-z_]+$/.test(formData.code)) {
      newErrors.code = 'Le code doit être au format "categorie.action" (ex: weighing.create)';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData.display_name.trim()) {
      newErrors.display_name = 'Le nom d\'affichage est requis';
    }

    if (!formData.category.trim()) {
      newErrors.category = 'La catégorie est requise';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof PermissionFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {permission ? 'Modifier la permission' : 'Créer une nouvelle permission'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Code */}
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
              Code de la permission *
            </label>
            <input
              type="text"
              id="code"
              value={formData.code || ''}
              onChange={(e) => handleInputChange('code', e.target.value)}
              placeholder="ex: weighing.create"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.code ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={!!permission} // Ne pas permettre la modification du code
            />
            {errors.code && <p className="mt-1 text-sm text-red-600">{errors.code}</p>}
            <p className="mt-1 text-sm text-gray-500">
              Format: catégorie.action (ex: weighing.create, stock.view)
            </p>
          </div>

          {/* Nom */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Nom de la permission *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name || ''}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="ex: weighing.create"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Nom d'affichage */}
          <div>
            <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 mb-2">
              Nom d'affichage *
            </label>
            <input
              type="text"
              id="display_name"
              value={formData.display_name || ''}
              onChange={(e) => handleInputChange('display_name', e.target.value)}
              placeholder="ex: Créer une pesée"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.display_name ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.display_name && <p className="mt-1 text-sm text-red-600">{errors.display_name}</p>}
          </div>

          {/* Catégorie */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
              Catégorie *
            </label>
            <select
              id="category"
              value={formData.category || ''}
              onChange={(e) => handleInputChange('category', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.category ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Sélectionner une catégorie</option>
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
            {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
          </div>

          {/* Niveau d'accès */}
          <div>
            <label htmlFor="access_level" className="block text-sm font-medium text-gray-700 mb-2">
              Niveau d'accès *
            </label>
            <select
              id="access_level"
              value={formData.access_level || 'read'}
              onChange={(e) => handleInputChange('access_level', e.target.value as 'read' | 'write' | 'admin')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {accessLevels.map(level => (
                <option key={level.value} value={level.value}>{level.label}</option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Description détaillée de la permission"
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.description ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Sauvegarde...' : (permission ? 'Modifier' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PermissionModal;
